import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Table, { TableColumn } from '../../components/Table/Table';
import { CrewList, CrewChange } from '../../types';
import { useEmployees } from '../../hooks/useEmployees';
import { useBranches } from '../../hooks/useBranches';
import { useCrewChanges } from '../../hooks/useCrewChanges';
import { PageHeader, StatsCards } from '../../components/Common';
import type { StatCard } from '../../components/Common';
import CrewVesselInfoForm from './CrewVesselInfoForm';
import CrewActionButtons from './CrewActionButtons';
import { generateCrewListPrintHtml, generateCrewListWordHtml } from './CrewListPrintTemplateAr';
import { arabicEmployeeName } from '../../utils/employeeName';

const CrewListArabic: React.FC = () => {
  const { data: employees = [] } = useEmployees({ populate: '*' });
  const { data: branches = [] } = useBranches({ populate: '*' });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCrewList, setSelectedCrewList] = useState<string[]>([]);
  const [vesselName, setVesselName] = useState('');
  const [callSignAr, setCallSignAr] = useState('');
  const [regNumber, setRegNumber] = useState('');
  const [vesselLocationAr, setVesselLocationAr] = useState('');
  const [vesselFlagAr, setVesselFlagAr] = useState('مصر');

  const { data: crewChangesData = [] } = useCrewChanges({ pagination: { pageSize: 1000 } });
  const crewChanges = crewChangesData as CrewChange[];

  const [activeCrewList, setActiveCrewList] = useState<CrewList[]>([]);

  const findEmployee = useCallback((employeeId: string) => {
    return employees.find((e: any) => e.id?.toString() === employeeId || e.documentId === employeeId);
  }, [employees]);

  const getEmployeeName = useCallback((employeeId: string) => arabicEmployeeName(findEmployee(employeeId)) || '-', [findEmployee]);
  const getEmployeePosition = useCallback((employeeId: string) => findEmployee(employeeId)?.position || '-', [findEmployee]);
  const getEmployeeRank = useCallback((employeeId: string) => {
    const emp = findEmployee(employeeId);
    return emp ? (emp.rank || emp.position || '-') : '-';
  }, [findEmployee]);
  const getEmployeeNationality = useCallback((employeeId: string) => findEmployee(employeeId)?.nationality || '-', [findEmployee]);
  const getEmployeeBirthDate = useCallback((employeeId: string) => {
    const emp = findEmployee(employeeId);
    return emp?.birthDate ? new Date(emp.birthDate).toLocaleDateString('ar-EG') : '-';
  }, [findEmployee]);
  const getEmployeeSeamanBookNumber = useCallback((employeeId: string) => findEmployee(employeeId)?.seamanBookNumber || '-', [findEmployee]);
  const getEmployeeSeamanBookExpiry = useCallback((employeeId: string) => {
    const emp = findEmployee(employeeId);
    return emp?.seamanBookExpiry ? new Date(emp.seamanBookExpiry).toLocaleDateString('ar-EG') : '-';
  }, [findEmployee]);

  const getBranchInfo = useCallback((branchId: string) => {
    const branch = branches.find((b: any) => b.id?.toString() === branchId || b.documentId === branchId);
    if (!branch) return '-';
    return branch.type === 'vessel' ? `${branch.name} (سفينة)` : branch.name;
  }, [branches]);

  const getDaysOnBoard = (joinDate: string) => {
    const join = new Date(joinDate);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - join.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  useEffect(() => {
    const employeeLatestChanges: { [key: string]: CrewChange } = {};
    crewChanges.forEach(change => {
      const existing = employeeLatestChanges[change.employeeId!];
      if (!existing || new Date(change.createdAt!) > new Date(existing.createdAt!)) {
        employeeLatestChanges[change.employeeId!] = change;
      }
    });
    // Active = the employee's latest crew change is Sign On or Continue.
    // Immigration Sign On/Off are paperwork flags only — they must not gate
    // crew membership (a "continue" row often has neither box checked).
    const activeChanges = Object.values(employeeLatestChanges).filter(
      change => change.status === 'sign_on' || change.status === 'continue'
    );
    const crewList: CrewList[] = activeChanges.map(change => ({
      id: `crew_${change.employeeId!}`,
      employeeId: change.employeeId!,
      joinDate: change.changeDate,
      branchId: change.branchId!,
      position: getEmployeePosition(change.employeeId!),
      status: 'active'
    }));
    setActiveCrewList(crewList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crewChanges]);

  const filteredActiveCrewList = useMemo(() => {
    if (!searchTerm) return activeCrewList;
    return activeCrewList.filter(crew => {
      return getEmployeeName(crew.employeeId).toLowerCase().includes(searchTerm.toLowerCase()) ||
             getBranchInfo(crew.branchId).toLowerCase().includes(searchTerm.toLowerCase()) ||
             getEmployeePosition(crew.employeeId).toLowerCase().includes(searchTerm.toLowerCase()) ||
             getEmployeeRank(crew.employeeId).toLowerCase().includes(searchTerm.toLowerCase());
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCrewList, searchTerm]);

  const handleCrewToggle = (crewId: string) => {
    setSelectedCrewList(prev =>
      prev.includes(crewId) ? prev.filter(id => id !== crewId) : [...prev, crewId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCrewList.length === filteredActiveCrewList.length) {
      setSelectedCrewList([]);
    } else {
      setSelectedCrewList(filteredActiveCrewList.map(crew => crew.id));
    }
  };

  const getSelectedCrewRows = () => {
    const selectedCrewData = activeCrewList.filter(crew => selectedCrewList.includes(crew.id));
    return selectedCrewData.map(crew => ({
      name: getEmployeeName(crew.employeeId),
      rank: getEmployeeRank(crew.employeeId),
      nationality: getEmployeeNationality(crew.employeeId),
      birthDate: getEmployeeBirthDate(crew.employeeId),
      seamanBookNumber: getEmployeeSeamanBookNumber(crew.employeeId),
      seamanBookExpiry: getEmployeeSeamanBookExpiry(crew.employeeId),
      joinDate: new Date(crew.joinDate).toLocaleDateString('ar-EG')
    }));
  };

  const vesselFormData = { vesselName, callSign: callSignAr, regNumber, location: vesselLocationAr, flag: vesselFlagAr };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(generateCrewListPrintHtml(vesselFormData, getSelectedCrewRows()));
      printWindow.document.close();
    }
  };

  const handleDownloadWord = () => {
    const wordContent = generateCrewListWordHtml(vesselFormData, getSelectedCrewRows());
    const blob = new Blob([wordContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `crew_list_arabic_${new Date().toISOString().split('T')[0]}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleEmailWord = () => {
    alert('وظيفة إرسال البريد الإلكتروني ستكون متاحة قريباً');
  };

  const columns: TableColumn[] = [
    { key: 'select', label: 'اختيار', sortable: false, render: (_value: any, row: CrewList) => (
      <input type="checkbox" checked={selectedCrewList.includes(row.id)} onChange={() => handleCrewToggle(row.id)}
        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
    )},
    { key: 'employeeId', label: 'اسم الموظف', sortable: true, render: (_: string, item: CrewList) => getEmployeeName(item.employeeId!) },
    { key: 'position', label: 'المنصب', sortable: true },
    { key: 'employeeId', label: 'الرتبة', sortable: true, render: (_: string, item: CrewList) => getEmployeeRank(item.employeeId!) },
    { key: 'branchId', label: 'الفرع الحالي', sortable: true, render: (_: string, item: CrewList) => getBranchInfo(item.branchId!) },
    { key: 'joinDate', label: 'تاريخ الالتحاق', sortable: true, render: (value: string) => new Date(value).toLocaleDateString('ar-EG') },
    { key: 'joinDate', label: 'أيام على متن السفينة', sortable: true, render: (value: string) => `${getDaysOnBoard(value)} يوم` },
    { key: 'status', label: 'الحالة', sortable: true, render: (value: string) => (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        {value === 'active' ? 'نشط' : 'غير نشط'}
      </span>
    )}
  ];

  const stats: StatCard[] = [
    { label: 'إجمالي الطاقم النشط', value: activeCrewList.length, color: 'blue' },
    { label: 'تم تحديدهم', value: selectedCrewList.length, color: 'green' },
    { label: 'نتائج البحث', value: filteredActiveCrewList.length, color: 'purple' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="قائمة الطاقم النشط (عربي)"
        description="إدارة وطباعة قائمة الطاقم النشط باللغة العربية"
      />

      <CrewVesselInfoForm
        vesselName={vesselName} onVesselNameChange={setVesselName}
        callSignAr={callSignAr} onCallSignChange={setCallSignAr}
        regNumber={regNumber} onRegNumberChange={setRegNumber}
        vesselLocationAr={vesselLocationAr} onLocationChange={setVesselLocationAr}
        vesselFlagAr={vesselFlagAr} onFlagChange={setVesselFlagAr}
      />

      <CrewActionButtons
        selectedCount={selectedCrewList.length}
        totalCount={filteredActiveCrewList.length}
        allSelected={selectedCrewList.length === filteredActiveCrewList.length}
        onSelectAll={handleSelectAll}
        onPrint={handlePrint}
        onDownloadWord={handleDownloadWord}
        onEmailWord={handleEmailWord}
      />

      <div className="bg-white rounded-lg shadow">
        <Table
          columns={columns}
          data={filteredActiveCrewList}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="البحث في الطاقم..."
          title="قائمة الطاقم النشط"
          showAddButton={false}
          showEditButton={false}
          showDeleteButton={false}
        />
      </div>

      <StatsCards stats={stats} columns={3} />
    </div>
  );
};

export default CrewListArabic;
